import React, { useCallback, useMemo } from 'react';
import { Check } from '../../icons';
import { getAllByType } from '../../utils';
import {
  Container,
  StepContent,
  StepHeader,
  StepHeaderConnector,
  StepHeaderDescription,
  StepHeaderText,
  StepHeaderTitle,
  StepIconCircle,
  StepsHeader,
} from './Stepper.styles';
import { StepperProps, StepperSubComponents, StepType } from './Stepper.types';

/* istanbul ignore next */
const Step: React.FC = () => null;
Step.displayName = 'Step';

const Stepper: React.FC<StepperProps> & StepperSubComponents = ({
  current,
  children,
  ...props
}) => {
  const stepsHeaders = useMemo(() => {
    const steps = getAllByType(children, Step);
    if (!steps || steps.length === 0) {
      return [];
    }

    return steps.map((step, i) => ({
      index: i,
      title: step.props.title,
      description: step.props.description,
      content: step.props.children,
    }));
  }, [children]);

  const currentStepContent = useMemo(() => {
    const steps = getAllByType(children, Step);
    if (!steps || steps.length === 0) {
      return null;
    }

    const currentStep = steps.find((_step, i) => i === current - 1);
    return currentStep?.props.children;
  }, [children, current]);

  const getCurrentState = useCallback(
    (index): StepType => {
      if (index + 1 === current) {
        return 'CURRENT';
      }
      if (index + 1 > current) {
        return 'UNFINISHED';
      }
      return 'FINISHED';
    },
    [current],
  );

  return (
    <Container {...props}>
      <StepsHeader>
        {stepsHeaders.map(stepHeader => (
          <StepHeader
            data-testid={stepHeader.title}
            state={getCurrentState(stepHeader.index)}
            key={stepHeader.index}
          >
            <StepIconCircle
              data-testid={`${stepHeader.title}-icon`}
              state={getCurrentState(stepHeader.index)}
              style={{
                flexDirection: 'column',
              }}
            >
              {getCurrentState(stepHeader.index) === 'FINISHED' ? <Check /> : stepHeader.index + 1}
            </StepIconCircle>
            <StepHeaderText state={getCurrentState(stepHeader.index)}>
              <StepHeaderTitle state={getCurrentState(stepHeader.index)}>
                <p>{stepHeader.title}</p>
              </StepHeaderTitle>
              <StepHeaderDescription>
                <p>{stepHeader.description}</p>
              </StepHeaderDescription>
            </StepHeaderText>
            {stepHeader.index !== stepsHeaders.length - 1 && (
              <StepHeaderConnector
                state={stepHeader.index < current - 1 ? 'FINISHED' : 'UNFINISHED'}
              />
            )}
          </StepHeader>
        ))}
      </StepsHeader>
      <StepContent>{currentStepContent}</StepContent>
    </Container>
  );
};

Stepper.Step = Step;

export { Stepper };
